import React, {
    createContext,
    memo, ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { RiFileList2Line, RiNodeTree, RiSaveLine } from "@remixicon/react";
import Confirm from "../../confirm/Confirm";
import WorkflowHeader from "../components/WorkflowHeader";
import ButtonTabs from "../../theme/button/ButtonTabs";
import Icon from "../../theme/icon/Icon";
import Toggle from "../../theme/toggle/Toggle";
import Button from "../../theme/button/Button";
import Styles from "./WorkflowModalProvider.module.scss";
import Help from "../../help/Help";
import {
    FormFieldDescriptorInput, useSaveWorkflowMutation, Workflow, WorkflowTriggerType, WorkflowStep,
} from "../../../__generated__/api";
import { Property } from "../../dirty-tracker/FormTracker";
import { createWorklowStep } from "../../workflow/Workflow";
import WORKFLOW_TRIGGER_TYPES from "../../../pages/administrator/sections/request-form/WorkflowTriggerTypes";
import {
    PropertiesByTrigger,
} from "../../../pages/administrator/sections/request-form/general-settings/useGeneralSettingsProperties";
import sanitizeWorkflowSteps from "../workflow/sanitize-flow";

export enum SidebarContent {
    ACTIONS = "ACTIONS",
    FORM_BUILDER = "FORM_BUILDER",
}

export interface FormDesignerValues {
    fields?: FormFieldDescriptorInput[];
    externalFormId?: string;
}

interface WorkflowModalContextType {
    isSidebarOpen: boolean;
    setIsSidebarOpen: (isSidebarOpen: boolean) => void;
    sidebarContent: SidebarContent;
    setSidebarContent: (sidebarContent: SidebarContent) => void;
    formDesignerProperties: {
        fields: Property<FormFieldDescriptorInput[]>;
        externalFormId: Property<any>;
    } | undefined;
    draftWorkflow: Workflow | undefined;
    setDraftWorkflow: (draftWorkflow: Workflow | undefined) => void;
}

export const WorkflowModalContext = createContext<WorkflowModalContextType>({
    isSidebarOpen: false,
    setIsSidebarOpen: () => {
        // do nothing
    },
    sidebarContent: SidebarContent.ACTIONS,
    setSidebarContent: () => {
        // do nothing
    },
    formDesignerProperties: {
        fields: new Property<FormFieldDescriptorInput[]>(() => [], () => []),
        externalFormId: new Property<string>(() => "", () => ""),
    },
    draftWorkflow: undefined,
    setDraftWorkflow: () => {
        // do nothing
    },
});

export enum AutomationTab {
    GENERAL_DETAILS = "GENERAL_DETAILS",
    AUTOMATION = "AUTOMATION",
}

const TABS = [
    {
        key: AutomationTab.GENERAL_DETAILS,
        text: (
            <>
                <Icon icon={<RiFileList2Line />} size="small" />
                {" "}
                General Details
            </>
        ),
    },
    {
        key: AutomationTab.AUTOMATION,
        text: (
            <>
                <Icon icon={<RiNodeTree />} size="small" />
                {" "}
                Automation Builder
            </>
        ),
    },
];

interface WorkflowModalProviderProps {
    children: React.ReactNode;
    title?: string;
    onHide?: () => void;
    generalDetailsPanel?: ReactNode;
    workflow?: Workflow;
    onWorkflowInit?: () => void;
    formDesignerProperties?: {
        fields: Property<FormFieldDescriptorInput[]>;
        externalFormId: Property<any>;
    };
    startTab?: AutomationTab;
    generalSettingsProperties?: PropertiesByTrigger<WorkflowTriggerType>;
}

function WorkflowModalProvider({
    children,
    title,
    onHide,
    generalDetailsPanel,
    onWorkflowInit,
    formDesignerProperties,
    startTab,
    workflow,
    generalSettingsProperties,
}: WorkflowModalProviderProps) {
    const [saveWorkflow] = useSaveWorkflowMutation();

    const [isModalInitialized, setIsModalInitialized] = useState(false);
    const [activeTab, setActiveTab] = useState(startTab ?? AutomationTab.AUTOMATION);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [sidebarContent, setSidebarContent] = useState<SidebarContent>(SidebarContent.ACTIONS);
    const [draftWorkflow, setDraftWorkflow] = useState(() => structuredClone(workflow as Workflow | undefined));

    useEffect(() => {
        setDraftWorkflow(structuredClone(workflow as Workflow | undefined));
    }, [workflow]);

    const onHideModal = useCallback(() => {
        onHide?.();
    }, [onHide]);

    const handleSaveWorkflow = useCallback(async () => {
        const workflowToSave = draftWorkflow ?? workflow;
        if (!workflowToSave) {
            return;
        }
        const sanitizedSteps = sanitizeWorkflowSteps(workflowToSave.steps as WorkflowStep[]);

        await saveWorkflow({
            variables: {
                id: workflow?.id,
                workflow: {
                    name: generalSettingsProperties?.name.value ?? workflow?.name,
                    description: generalSettingsProperties?.description.value ?? workflow?.description,
                    steps: sanitizedSteps.map(createWorklowStep),
                },
            },
        });
        setIsSidebarOpen(false);
        onWorkflowInit?.();
    }, [draftWorkflow, workflow, saveWorkflow, onWorkflowInit, setIsSidebarOpen]);

    const contextValue = useMemo(
        () => ({
            isSidebarOpen,
            setIsSidebarOpen,
            sidebarContent,
            setSidebarContent,
            formDesignerProperties,
            draftWorkflow,
            setDraftWorkflow,
        }),
        [
            isSidebarOpen,
            setIsSidebarOpen,
            sidebarContent,
            setSidebarContent,
            formDesignerProperties,
            draftWorkflow,
            setDraftWorkflow,
        ],
    );

    const content = useMemo(
        () => (activeTab === AutomationTab.GENERAL_DETAILS ? generalDetailsPanel : children),
        [activeTab, generalDetailsPanel, children],
    );

    const shouldShowFormToggle = useMemo(() => {
        const formType = draftWorkflow?.triggers?.firstElement?.type;
        if (activeTab !== AutomationTab.AUTOMATION || !formType) {
            return false;
        }

        return WORKFLOW_TRIGGER_TYPES[formType].hasFormBuilder;
    }, [draftWorkflow, activeTab]);

    return (
        <WorkflowModalContext.Provider value={contextValue}>
            <Confirm
                shouldShow
                size="xl"
                fullscreen="true"
                dialogClassName="full-size-container"
                keyboardContext="workflow"
                onHide={onHideModal}
                shouldShowFooter={false}
                closeText="Close"
                headerControls={(
                    <WorkflowHeader
                        controls={(
                            <>
                                {
                                    generalDetailsPanel && (
                                        <div className={Styles.tabs}>
                                            <ButtonTabs
                                                onChange={item => setActiveTab(item.key)}
                                                value={activeTab ?? AutomationTab.AUTOMATION}
                                                tabVariant="default"
                                                values={TABS}
                                                size="xsmall"
                                            />
                                        </div>
                                    )
                                }
                                <div>
                                    {
                                        shouldShowFormToggle && (
                                            <Toggle
                                                checked={isSidebarOpen && sidebarContent === SidebarContent.FORM_BUILDER}
                                                onChange={event => {
                                                    const { checked } = event.target as HTMLInputElement;
                                                    setIsSidebarOpen(checked);
                                                    if (checked) {
                                                        setSidebarContent(SidebarContent.FORM_BUILDER);
                                                    }
                                                }}
                                                text={(
                                                    <>
                                                        Show Form Builder
                                                        {" "}
                                                        <Help buttonType="small-icon" />
                                                    </>
                                                )}
                                                size="small"
                                                textVariant="primary"
                                            />
                                        )
                                    }
                                </div>
                                <div className={Styles.actions}>
                                    <Button
                                        onClick={handleSaveWorkflow}
                                        variant="primary"
                                        size="small"
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        onClick={async () => {
                                            handleSaveWorkflow();
                                            onHideModal();
                                        }}
                                        prefixIcon={<RiSaveLine />}
                                        variant="primary"
                                        size="small"
                                    >
                                        Save & Publish
                                    </Button>
                                </div>
                            </>
                        )}
                        title={title}
                    />
                )}
                bodyClassName="p-0"
                onEntered={() => {
                    setIsModalInitialized(true);
                }}
            >
                {isModalInitialized && content}
            </Confirm>
        </WorkflowModalContext.Provider>
    );
}

WorkflowModalProvider.defaultProps = {
    onHide: undefined,
    title: undefined,
    generalDetailsPanel: undefined,
    workflow: undefined,
    onWorkflowInit: undefined,
    formDesignerProperties: undefined,
    startTab: undefined,
    generalSettingsProperties: undefined,
};

export default memo(WorkflowModalProvider);

export function useWorkflowModal() {
    return useContext(WorkflowModalContext);
}
